import React, { useEffect, useMemo, useState } from 'react';
import { useIntl, IntlShape } from 'react-intl';
import { IconCalendar } from '@wheely/ui-kit';
import parse from 'date-fns/esm/parse';

import { TextField as OriginalField } from './TextField';
import type { ITextFieldProps } from './TextField';

const leftIcon = <IconCalendar />;

const REFERENCE_DATE = new Date();

const DATE_SEPARATORS = new Set(['.', ' ', '-', '\\', '/']);

const isValidDate = (date: Date) => !Number.isNaN(date.valueOf());

const extractDateFormat = (intl: IntlShape) =>
  intl
    .formatDate(new Date(2222, 10, 30))
    .replace('2222', 'yyyy')
    .replace('11', 'MM')
    .replace('30', 'dd');

const checkIsDigit = (symbol: string) => symbol[0] === Number(symbol[0]).toString();

const tryAutoComplete = (textValue: string, nextTextValue: string, pattern: string) => {
  if (nextTextValue.length - textValue.length === 1 && nextTextValue.startsWith(textValue)) {
    const lastSymbolIdx = nextTextValue.length - 1;
    const lastSymbol = nextTextValue[lastSymbolIdx];

    if (!checkIsDigit(lastSymbol) && !DATE_SEPARATORS.has(lastSymbol)) {
      return textValue;
    }

    const currentPatternSymbol = pattern[lastSymbolIdx];
    const nextPatternSymbol = pattern[lastSymbolIdx + 1];

    if (currentPatternSymbol !== lastSymbol && DATE_SEPARATORS.has(currentPatternSymbol)) {
      nextTextValue = textValue + currentPatternSymbol + lastSymbol;
    } else if (DATE_SEPARATORS.has(nextPatternSymbol)) {
      nextTextValue += nextPatternSymbol;
    } else if (nextTextValue.length > pattern.length) {
      nextTextValue = textValue;
    }
  }

  return nextTextValue;
};

const checkIsDate = (date: any): date is Date => date instanceof Date && isValidDate(date);

const EMPTY_VALUE = null;

type TMaybeDate = Date | null;

interface IProps<TName extends string>
  extends Omit<ITextFieldProps<TName>, 'value' | 'onChange' | 'inputRef'> {
  value: TMaybeDate;
  onChange(
    event: React.ChangeEvent<HTMLInputElement>,
    { value, name }: { value: TMaybeDate; name: TName },
  ): void;
  onChangeRaw?(event: React.ChangeEvent<HTMLInputElement>): void;
  onBlur?(event: React.FocusEvent): void;
  inputRef?: React.Ref<HTMLInputElement>;
}

const DateField = <TName extends string>({
  value,
  name,
  onChange,
  onBlur,
  inputRef,
  error,
  description,
  ...restProps
}: IProps<TName>) => {
  const intl = useIntl();

  if (value !== null && !checkIsDate(value)) {
    throw new Error(`Date input expect only Date or null value: ${typeof value}`);
  }

  const [textValue, setTextValue] = useState<string>(
    checkIsDate(value) ? intl.formatDate(value) : '',
  );
  const [isFormatSuggestVisible, setIsFormatSuggestVisible] = useState(false);

  useEffect(() => {
    setTextValue(checkIsDate(value) ? intl.formatDate(value) : '');
  }, [intl, value]);

  const pattern = useMemo(() => extractDateFormat(intl), [intl]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    { value: inputTextValue }: { value: string },
  ) => {
    const nextTextValue = tryAutoComplete(textValue, inputTextValue, pattern);

    if (nextTextValue === textValue) {
      return;
    }

    setTextValue(nextTextValue);

    if (nextTextValue === '') {
      setIsFormatSuggestVisible(false);
      onChange(event, { value: EMPTY_VALUE, name });

      return;
    }

    const parsedDate = parse(nextTextValue, pattern, REFERENCE_DATE);

    if (isValidDate(parsedDate) && parsedDate.getFullYear() > 1000) {
      onChange(event, { value: parsedDate, name });
      setIsFormatSuggestVisible(false);
    } else {
      setIsFormatSuggestVisible(true);
    }
  };

  const handleBlur = (event: React.FocusEvent) => {
    onBlur?.(event);
    setIsFormatSuggestVisible(false);
    setTextValue(checkIsDate(value) ? intl.formatDate(value) : '');
  };

  const placeholder = pattern.toUpperCase();

  return (
    <OriginalField
      {...restProps}
      name={name}
      startElem={leftIcon}
      inputRef={inputRef ?? undefined}
      onChange={handleChange}
      onBlur={handleBlur}
      value={textValue}
      placeholder={placeholder}
      error={error}
      dataTestId="form-date-field"
      inputDataTestId="date-input"
      description={(() => {
        if (error) {
          return undefined;
        }

        if (isFormatSuggestVisible) {
          return intl.formatMessage(
            {
              defaultMessage: 'Please use format {format}, ex. {example}',
              id: 'RE1xCE',
              description: 'Date input tip',
            },
            {
              format: placeholder,
              example: intl.formatDate(new Date()),
            },
          );
        }

        return description;
      })()}
    />
  );
};

export { DateField };
